/**
 * @td/i18n — typed FR/EN catalogs, LAZY-LOADED at runtime (the inactive locale
 * must not weigh on the core shell — ~10 KB gzip each; the audit's reclaim).
 *
 * The type system is STILL the coverage gate (compile-time, zero runtime cost):
 *  - `MessageKey` = keys of the English reference catalog (type-only import —
 *    no catalog value is pulled into the shell here);
 *  - `fr.ts satisfies Record<keyof typeof en, string>` → a missing/extra key is
 *    a COMPILE error;
 *  - `t()` only accepts `MessageKey`.
 *
 * At RUNTIME each catalog is a dynamic-import chunk. `loadCatalog(locale)` is
 * awaited ONCE before the first paint (main.tsx boot) and on locale switch
 * (App.tsx), so `t()` stays synchronous. Before a catalog is loaded, `t()`
 * returns the key itself — loud and greppable, never a crash.
 */
import type { en } from "./catalogs/en.ts"; // TYPE ONLY — keeps the catalog out of the shell

export const LOCALES = ["en", "fr"] as const;
export type Locale = (typeof LOCALES)[number];

/** Every message key in the app. Derived from the English reference catalog. */
export type MessageKey = keyof typeof en;

export const DEFAULT_LOCALE: Locale = "en";

type Catalog = Readonly<Record<MessageKey, string>>;

/** Loaded catalogs, filled by loadCatalog(). Empty until boot loads the active
 * locale. Only the active locale is ever loaded — that IS the reclaim. */
const store: Partial<Record<Locale, Catalog>> = {};

/**
 * Load a locale's catalog (a lazy chunk). Idempotent. MUST be awaited before
 * the first render of that locale (boot + locale switch), so `t()` is ready.
 */
export async function loadCatalog(locale: Locale): Promise<void> {
  if (store[locale] !== undefined) return;
  // Split the branches so each dynamic-import type narrows to its own module
  // (a shared `mod` would be the union, where neither .fr nor .en is provable).
  if (locale === "fr") {
    store.fr = (await import("./catalogs/fr.ts")).fr as Catalog;
  } else {
    store.en = (await import("./catalogs/en.ts")).en as Catalog;
  }
}

export function isCatalogLoaded(locale: Locale): boolean {
  return store[locale] !== undefined;
}

export function isLocale(s: string): s is Locale {
  return (LOCALES as readonly string[]).includes(s);
}

/** Best-effort locale from a BCP-47 tag ("fr-FR" → "fr"); default EN. */
export function localeFromLanguageTag(tag: string | undefined | null): Locale {
  const primary = (tag ?? "").toLowerCase().split("-")[0] ?? "";
  return isLocale(primary) ? primary : DEFAULT_LOCALE;
}

/** Whether `k` is a known message key — checks any loaded catalog (best-effort;
 * decides only whether a registry label is translatable, see tLabel). */
export function isMessageKey(k: string): k is MessageKey {
  const cat = store[DEFAULT_LOCALE] ?? Object.values(store)[0];
  return cat !== undefined && Object.prototype.hasOwnProperty.call(cat, k);
}

/**
 * Translate a message key, substituting `{name}` placeholders from `params`.
 * Unknown placeholders are left as-is (loud in the UI, greppable). Before the
 * catalog is loaded, returns the key itself (never crashes).
 */
export function t(
  locale: Locale,
  key: MessageKey,
  params?: Readonly<Record<string, string | number>>,
): string {
  const cat = store[locale] ?? store[DEFAULT_LOCALE];
  const template = cat !== undefined ? cat[key] : key;
  if (params === undefined) return template;
  const p = params; // const binding: narrowing survives into the closure
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    Object.prototype.hasOwnProperty.call(p, name) ? String(p[name]) : whole,
  );
}

/**
 * Translate a registry labelKey (typed `string` at the registry boundary).
 * An unknown key renders AS the key itself — visible and greppable, never a
 * crash; the coverage test guarantees this branch is dead for registry keys.
 */
export function tLabel(locale: Locale, key: string): string {
  const cat = store[locale] ?? store[DEFAULT_LOCALE];
  return cat !== undefined && Object.prototype.hasOwnProperty.call(cat, key)
    ? t(locale, key as MessageKey)
    : key;
}

/** Bind a locale once; hand the resulting function to render code. */
export function makeT(locale: Locale): {
  t: (key: MessageKey, params?: Readonly<Record<string, string | number>>) => string;
  tLabel: (key: string) => string;
} {
  return {
    t: (key, params) => t(locale, key, params),
    tLabel: (key) => tLabel(locale, key),
  };
}
